import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

const backend_domain = process.env.REACT_APP_BACKEND_DOMAIN;
const backend_port = process.env.REACT_APP_BACKEND_PORT;

export default function PaymentCompletion() {
  const [searchParams] = useSearchParams();
  const { getAccessTokenSilently, user } = useAuth0();
  const [submission, setSubmission] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<number | null>(null);

  useEffect(() => {
    const fetchEnvolvementDetails = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        const paymentIntent = searchParams.get("payment_intent");

        if (!paymentIntent) {
          setError("No payment intent found");
          setLoading(false);
          return;
        }

        // Try to fetch envolvement details, with retries
        let retries = 3;
        let success = false;

        while (retries > 0 && !success) {
          try {
            const response = await fetch(
              `${backend_domain}${backend_port}/payment/get-submission/${paymentIntent}`,
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              }
            );

            if (response.ok) {
              const data = await response.json();
              setSubmission(data);

              // Get payment amount from the payment intent
              const paymentResponse = await fetch(
                `${backend_domain}${backend_port}/payment/get-payment-amount/${paymentIntent}`,
                {
                  headers: {
                    Authorization: `Bearer ${accessToken}`,
                  },
                }
              );

              if (paymentResponse.ok) {
                const paymentData = await paymentResponse.json();
                setPaymentAmount(paymentData.amount / 100); // Convert from cents to dollars
              }

              // Grant access to the user
              const grantAccessResponse = await fetch(
                `${backend_domain}${backend_port}/payment/grant-access`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                  },
                  body: JSON.stringify({
                    submissionId: data._id,
                    userId: user?.sub,
                  }),
                }
              );

              if (!grantAccessResponse.ok) {
                console.error("Failed to grant access");
              }

              success = true;
            } else if (retries === 1) {
              throw new Error("Failed to fetch envolvement details");
            }
          } catch (err) {
            if (retries === 1) {
              throw err;
            }
            // Wait for 1 second before retrying
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
          retries--;
        }
      } catch (err) {
        console.error("Error fetching envolvement:", err);
        setError(
          "Failed to load envolvement details. Please try refreshing the page."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchEnvolvementDetails();
  }, [searchParams, getAccessTokenSilently, user?.sub]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <div className="text-xl text-red-600">{error}</div>
        <Link to="/" className="mt-4 text-blue-600 hover:underline">
          Return to Home
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="text-center mb-6">
          <svg
            className="mx-auto h-12 w-12 text-green-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
          <h1 className="text-2xl font-bold mt-4">Payment Successful!</h1>
          <p className="text-gray-600 mt-2">
            Thank you for your purchase. You now have access to the content.
          </p>
          {paymentAmount !== null && (
            <p className="text-lg font-semibold mt-2">
              Amount Paid: ${paymentAmount.toFixed(2)}
            </p>
          )}
        </div>

        {submission && (
          <div className="border-t pt-6">
            <h2 className="text-xl font-semibold mb-4">Purchase Details</h2>
            <div className="flex items-center space-x-4">
              <img
                src={submission.imgUrl}
                alt={submission.title}
                className="w-24 h-24 object-cover rounded-lg"
              />
              <div>
                <h3 className="font-medium">{submission.title}</h3>
                <p className="text-gray-600 text-sm">
                  By {submission.username}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 text-center">
          <Link
            to={`/envolvement/${submission?._id}`}
            className="inline-block bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
          >
            View Content
          </Link>
          <div className="mt-4">
            <Link to="/" className="text-blue-600 hover:underline">
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
